import { FC, ReactNode } from 'react';
import { MachineLearningModel } from '@carbon/react/icons';
import type { AuditAgentRunMeta } from './types';
import { formatDate } from './formatters';
import { RunPrompt } from './run-prompt';
import { ToolSummaryTable } from './tool-summary-table';
import { TimelineList } from './timeline-list';
import { ConversationTable } from './conversation-table';
import style from './audit-log.module.scss';

interface Props {
  /** Run metadata + transcript (everything except the suggestion list). */
  meta: AuditAgentRunMeta;
  /** Agent workflow process id, shown as the Run ID. */
  runId: number;
  /** Header for the suggestions section (e.g. "Suggestions Persisted"). */
  suggestionsLabel: string;
  /** Count shown beside the suggestions header. */
  suggestionsCount: number;
  /** Caller-rendered suggestion list (agent-specific row shape). */
  suggestions: ReactNode;
}

// Shared expanded body for an agent_run row: identity header, run-metadata grid,
// failure banner, then collapsible sections (suggestions slot + tool summary,
// tool calls, prompt, conversation). Agent-agnostic — the only varying piece is
// the `suggestions` slot supplied by each caller. A run that recorded no model
// or made no tool calls leaves those rows out.
export const AgentRunDetail: FC<Props> = ({
  meta: d,
  runId,
  suggestionsLabel,
  suggestionsCount,
  suggestions,
}) => {
  const modelBacked = !!d.model;
  const hasToolCalls =
    (d.toolSummary?.length ?? 0) > 0 || (d.timeline?.length ?? 0) > 0;
  return (
    <div className={style.expandedWrapper}>
      <div className={style.eventEntry}>
        <div className={`${style.eventIcon} ${style.eventIconSystem}`}>
          <MachineLearningModel size={16} />
        </div>
        <div>
          <p className={style.eventText}>
            <strong>{d.user || d.action}</strong> — {d.description}
          </p>
        </div>
      </div>

      <dl className={style.metaGrid}>
        <div>
          <dt>Status</dt>
          <dd>{d.processFailed ? 'Failed' : (d.status ?? '—')}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{d.durationFormatted ?? '—'}</dd>
        </div>
        {modelBacked && (
          <div>
            <dt>Model</dt>
            <dd>{d.model}</dd>
          </div>
        )}
        <div>
          <dt>Started</dt>
          <dd>{formatDate(d.startedAt)}</dd>
        </div>
        <div>
          <dt>Ended</dt>
          <dd>{formatDate(d.endedAt)}</dd>
        </div>
        <div>
          <dt>Run ID</dt>
          <dd>#{runId}</dd>
        </div>
      </dl>

      {d.processFailed && d.failureReason && (
        <div className={style.failureBox}>
          <strong>Failure:</strong> {d.failureReason}
        </div>
      )}

      <details className={style.rawBlockWrapper}>
        <summary className={style.rawBlockSummary}>
          {suggestionsLabel} ({suggestionsCount})
        </summary>
        <div className={style.rawBlockBody}>{suggestions}</div>
      </details>

      {(modelBacked || hasToolCalls) && (
        <>
          <details className={style.rawBlockWrapper}>
            <summary className={style.rawBlockSummary}>
              Tool Execution Summary
            </summary>
            <div className={style.rawBlockBody}>
              <ToolSummaryTable rows={d.toolSummary ?? []} />
            </div>
          </details>

          <details className={style.rawBlockWrapper}>
            <summary className={style.rawBlockSummary}>Tool Calls</summary>
            <div className={style.rawBlockBody}>
              <TimelineList rows={d.timeline ?? []} />
            </div>
          </details>
        </>
      )}

      {d.promptHref && <RunPrompt href={d.promptHref} />}

      {d.conversation && d.conversation.length > 0 && (
        <details className={style.rawBlockWrapper}>
          <summary className={style.rawBlockSummary}>
            Agent Conversation
          </summary>
          <ConversationTable entries={d.conversation} />
        </details>
      )}
    </div>
  );
};
